import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AppShell,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatusBadge,
  UIButton,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import {
  appendTicketUpdate,
  fetchTickets,
  getAuthHeadersOrRedirect,
  patchTicket,
} from './utils/ticketingApi';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { deriveCustomerImpactSummary } from './utils/customerImpact';
import CustomerImpactDrawer from './components/CustomerImpactDrawer';
import { createExecutionLog, fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import {
  RUNBOOK_PRESETS,
  TICKET_SHORTCUTS,
  buildRunbookMessage,
  buildTicketShortcutNote,
  shouldMirrorRunbookToTicket,
  shouldSkipRunbookDuplicate,
} from './utils/operationalRunbook';

const API = `${resolveBackendBaseUrl()}/api`;

const statusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'closed') return 'ok';
  if (token === 'mitigated') return 'info';
  if (token === 'investigating') return 'warn';
  return 'neutral';
};

const priorityTone = (priority) => {
  const token = String(priority || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const toContextSummary = (ticket) => {
  if (!ticket) return '-';
  const contextParts = [];
  if (ticket.sourceType && ticket.sourceId) {
    contextParts.push(`${ticket.sourceType}:${ticket.sourceId}`);
  }
  if (ticket.incidentId) contextParts.push(`incident:${ticket.incidentId}`);
  if (ticket.customerId) contextParts.push(`customer:${ticket.customerId}`);
  if (ticket.routerId) contextParts.push(`router:${ticket.routerId}`);
  if (ticket.siteId) contextParts.push(`site:${ticket.siteId}`);
  return contextParts.length ? contextParts.join(' | ') : '-';
};

const buildRecoveryLink = (ticket) => {
  if (!ticket?.incidentId) return '/recovery';
  return `/recovery?incidentId=${encodeURIComponent(String(ticket.incidentId))}`;
};

const buildTopologyLink = (ticket) => {
  if (!ticket) return '/topology?mode=live';
  const params = new URLSearchParams();
  params.set('mode', 'live');

  const sourceType = String(ticket.sourceType || '').toLowerCase();
  const sourceId = String(ticket.sourceId || '').trim();

  if (sourceType === 'router' || ticket.routerId) {
    params.set('sourceType', 'nas');
    params.set('sourceId', String(ticket.routerId || ticket.nodeId || sourceId || ''));
    if (ticket.routerId) params.set('routerId', String(ticket.routerId));
    params.set('openDetail', '1');
  } else if (sourceType === 'customer' && ticket.customerId) {
    params.set('sourceType', 'customer');
    params.set('sourceId', String(ticket.customerId));
  } else if (sourceType === 'site' && ticket.siteId) {
    params.set('siteId', String(ticket.siteId));
  } else if (sourceId && sourceType) {
    params.set('sourceType', sourceType);
    params.set('sourceId', sourceId);
  } else if (ticket.siteId) {
    params.set('siteId', String(ticket.siteId));
  }

  return `/topology?${params.toString()}`;
};

const buildInfrastructureLink = (ticket) => {
  if (ticket?.routerId || ticket?.nodeId) {
    const nodeId = String(ticket.routerId || ticket.nodeId || '');
    return `/infrastructure?nodeType=nas&nodeId=${encodeURIComponent(nodeId)}`;
  }
  if (ticket?.siteId) {
    return `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(ticket.siteId))}`;
  }
  return '/infrastructure';
};

const buildCustomerLink = (ticket) => {
  if (!ticket?.customerId) return '/customers';
  return `/customers?customerId=${encodeURIComponent(String(ticket.customerId))}`;
};

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

export default function AdminTickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customers, odps, olts, nasRouters, sites, incidents } = useOperationalDataset(navigate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [query, setQuery] = useState('');

  const [detailDraft, setDetailDraft] = useState({ status: 'open', assigned_to: '', priority: 'medium' });
  const [worklogNote, setWorklogNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [showImpactDrawer, setShowImpactDrawer] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');
  const [showAllExecutionLogs, setShowAllExecutionLogs] = useState(false);
  const [executionNote, setExecutionNote] = useState('');

  const loadTickets = useCallback(async () => {
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;

    setLoading(true);
    setError('');
    try {
      const payload = await fetchTickets({ headers, params: { limit: 500 } });
      setTickets(payload.items || []);
    } catch (err) {
      setError(String(err?.message || 'Gagal memuat daftar ticket.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const filteredTickets = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    return tickets.filter((ticket) => {
      const status = String(ticket.status || '').toLowerCase();
      const priority = String(ticket.priority || '').toLowerCase();
      const assignee = String(ticket.assignedTo || '').trim().toLowerCase() || '-';

      if (!includeClosed && statusFilter === 'all' && status === 'closed') return false;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (priorityFilter !== 'all' && priority !== priorityFilter) return false;
      if (assigneeFilter !== 'all' && assignee !== assigneeFilter) return false;

      if (!needle) return true;
      const haystack = [
        ticket.ticketCode,
        ticket.title,
        ticket.assignedTo,
        ticket.sourceType,
        ticket.sourceId,
        ticket.customerId,
        ticket.routerId,
        ticket.siteId,
      ].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [tickets, includeClosed, statusFilter, priorityFilter, assigneeFilter, query]);

  useEffect(() => {
    if (!filteredTickets.length) {
      setSelectedTicketId('');
      return;
    }
    const queryTicketId = String(new URLSearchParams(location.search || '').get('ticketId') || '').trim();
    if (queryTicketId) {
      const fromQuery = filteredTickets.find((ticket) => String(ticket.id) === queryTicketId || String(ticket.ticketCode || '') === queryTicketId);
      if (fromQuery) {
        setSelectedTicketId(String(fromQuery.id || ''));
        return;
      }
    }
    const selectedExists = filteredTickets.some((ticket) => String(ticket.id) === String(selectedTicketId));
    if (!selectedExists) {
      setSelectedTicketId(String(filteredTickets[0].id || ''));
    }
  }, [filteredTickets, selectedTicketId, location.search]);

  const selectedTicket = useMemo(
    () => filteredTickets.find((ticket) => String(ticket.id) === String(selectedTicketId)) || null,
    [filteredTickets, selectedTicketId],
  );

  const ticketImpactSummary = useMemo(() => {
    if (!selectedTicket) {
      return deriveCustomerImpactSummary({
        context: {},
        data: { customers, odps, olts, nasRouters, sites, incidents },
      });
    }
    const sourceType = String(selectedTicket.sourceType || '').toLowerCase();
    const sourceId = String(selectedTicket.sourceId || '').trim();
    const contextType = sourceType === 'nas' ? 'router' : sourceType;
    return deriveCustomerImpactSummary({
      context: {
        type: contextType || 'manual',
        id: sourceId,
        sourceType: contextType || 'manual',
        sourceId,
        incidentId: selectedTicket.incidentId || '',
        customerId: selectedTicket.customerId || '',
        routerId: selectedTicket.routerId || selectedTicket.nodeId || '',
        siteId: selectedTicket.siteId || '',
      },
      data: {
        customers,
        odps,
        olts,
        nasRouters,
        sites,
        incidents,
      },
    });
  }, [selectedTicket, customers, odps, olts, nasRouters, sites, incidents]);

  const executionContext = useMemo(() => {
    if (!selectedTicket) return null;
    return {
      sourceType: String(selectedTicket.sourceType || 'manual').toLowerCase(),
      sourceId: String(selectedTicket.sourceId || ''),
      incidentId: String(selectedTicket.incidentId || ''),
      ticketId: String(selectedTicket.id || ''),
      customerId: String(selectedTicket.customerId || ''),
      routerId: String(selectedTicket.routerId || selectedTicket.nodeId || ''),
      nodeId: String(selectedTicket.nodeId || selectedTicket.routerId || ''),
      siteId: String(selectedTicket.siteId || ''),
    };
  }, [selectedTicket]);

  useEffect(() => {
    if (!selectedTicket) {
      setDetailDraft({ status: 'open', assigned_to: '', priority: 'medium' });
      setShowAllLogs(false);
      setShowAllExecutionLogs(false);
      return;
    }
    setDetailDraft({
      status: String(selectedTicket.status || 'open').toLowerCase(),
      assigned_to: String(selectedTicket.assignedTo || ''),
      priority: String(selectedTicket.priority || 'medium').toLowerCase(),
    });
    setShowAllLogs(false);
  }, [selectedTicket]);

  useEffect(() => {
    let cancelled = false;
    const loadExecutionLogs = async () => {
      if (!executionContext) {
        setExecutionLogs([]);
        setExecutionError('');
        setExecutionLoading(false);
        return;
      }
      setExecutionLoading(true);
      setExecutionError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: {
            source_type: executionContext.sourceType,
            source_id: executionContext.sourceId,
            ticket_id: executionContext.ticketId || undefined,
            incident_id: executionContext.incidentId || undefined,
            customer_id: executionContext.customerId || undefined,
            router_id: executionContext.routerId || undefined,
            site_id: executionContext.siteId || undefined,
            limit: 120,
          },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (error) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionError(String(error?.message || 'Gagal memuat execution log.'));
      } finally {
        if (!cancelled) setExecutionLoading(false);
      }
    };
    loadExecutionLogs();
    return () => {
      cancelled = true;
    };
  }, [executionContext, navigate]);

  const activeCount = useMemo(
    () => tickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed').length,
    [tickets],
  );

  const filteredCounts = useMemo(() => {
    const base = { active: 0, investigating: 0, mitigated: 0, highCritical: 0 };
    filteredTickets.forEach((ticket) => {
      const status = String(ticket.status || '').toLowerCase();
      const priority = String(ticket.priority || '').toLowerCase();
      if (status !== 'closed') base.active += 1;
      if (status === 'investigating') base.investigating += 1;
      if (status === 'mitigated') base.mitigated += 1;
      if (priority === 'high' || priority === 'critical') base.highCritical += 1;
    });
    return base;
  }, [filteredTickets]);

  const assigneeOptions = useMemo(() => {
    const set = new Set();
    tickets.forEach((ticket) => {
      const value = String(ticket.assignedTo || '').trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  const priorityScore = (priority) => {
    const token = String(priority || '').toLowerCase();
    if (token === 'critical') return 3;
    if (token === 'high') return 2;
    if (token === 'medium') return 1;
    return 0;
  };

  const mostRelevantTicketId = useMemo(() => {
    const active = filteredTickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed');
    const ranked = active.slice().sort((a, b) => {
      const statusA = String(a.status || '').toLowerCase();
      const statusB = String(b.status || '').toLowerCase();
      const invA = statusA === 'investigating' ? 1 : 0;
      const invB = statusB === 'investigating' ? 1 : 0;
      if (invB !== invA) return invB - invA;
      const prio = priorityScore(b.priority) - priorityScore(a.priority);
      if (prio !== 0) return prio;
      const tsA = new Date(a.updated_at || a.created_at || 0).getTime();
      const tsB = new Date(b.updated_at || b.created_at || 0).getTime();
      return tsB - tsA;
    });
    return String(ranked[0]?.id || '');
  }, [filteredTickets]);

  const handleSaveDetail = async () => {
    if (!selectedTicket?.id) return;
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;

    setSaving(true);
    try {
      const updated = await patchTicket({
        headers,
        ticketId: selectedTicket.id,
        payload: {
          status: detailDraft.status,
          priority: detailDraft.priority,
          assigned_to: detailDraft.assigned_to || undefined,
        },
      });
      setTickets((prev) => prev.map((ticket) => (String(ticket.id) === String(updated.id) ? updated : ticket)));
      toast.success('Ticket berhasil diperbarui.');
    } catch (err) {
      toast.error(String(err?.message || 'Gagal memperbarui ticket.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddWorklog = async () => {
    if (!selectedTicket?.id) return;
    const note = String(worklogNote || '').trim();
    if (!note) {
      toast.error('Worklog tidak boleh kosong.');
      return;
    }
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;

    setSaving(true);
    try {
      const updated = await appendTicketUpdate({ headers, ticketId: selectedTicket.id, note });
      setTickets((prev) => prev.map((ticket) => (String(ticket.id) === String(updated.id) ? updated : ticket)));
      setWorklogNote('');
      toast.success('Worklog ticket tersimpan.');
    } catch (err) {
      toast.error(String(err?.message || 'Gagal menambah worklog.'));
    } finally {
      setSaving(false);
    }
  };

  const visibleLogs = useMemo(() => {
    const logs = Array.isArray(selectedTicket?.actionLog) ? selectedTicket.actionLog : [];
    const ordered = logs.slice().reverse();
    return showAllLogs ? ordered : ordered.slice(0, 3);
  }, [selectedTicket, showAllLogs]);

  const visibleExecutionLogs = useMemo(() => {
    const logs = Array.isArray(executionLogs) ? executionLogs : [];
    return showAllExecutionLogs ? logs : logs.slice(0, 5);
  }, [executionLogs, showAllExecutionLogs]);
  const latestExecutionLog = useMemo(
    () => (Array.isArray(executionLogs) && executionLogs.length > 0 ? executionLogs[0] : null),
    [executionLogs],
  );

  const handleAddExecutionNote = async () => {
    if (!executionContext) return;
    const note = String(executionNote || '').trim();
    if (!note) {
      toast.error('Catatan execution log belum diisi.');
      return;
    }
    setSaving(true);
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: executionContext.sourceType,
          source_id: executionContext.sourceId,
          incident_id: executionContext.incidentId || undefined,
          ticket_id: executionContext.ticketId || undefined,
          customer_id: executionContext.customerId || undefined,
          router_id: executionContext.routerId || undefined,
          node_id: executionContext.nodeId || undefined,
          site_id: executionContext.siteId || undefined,
          action_type: 'manual_note',
          message: note,
        },
      });
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      setExecutionNote('');
      toast.success('Execution log tersimpan.');
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menyimpan execution log.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRunbookPreset = async (preset) => {
    if (!executionContext || !preset) return;
    const message = buildRunbookMessage({
      presetId: preset.id,
      contextLabel: selectedTicket ? `${selectedTicket.ticketCode || selectedTicket.id}` : '',
    });
    if (shouldSkipRunbookDuplicate({ logs: executionLogs, actionType: preset.actionType || 'manual_note', message })) {
      toast.info('Preset sama baru saja dicatat. Menghindari log duplikat.');
      return;
    }
    setSaving(true);
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: executionContext.sourceType,
          source_id: executionContext.sourceId,
          incident_id: executionContext.incidentId || undefined,
          ticket_id: executionContext.ticketId || undefined,
          customer_id: executionContext.customerId || undefined,
          router_id: executionContext.routerId || undefined,
          node_id: executionContext.nodeId || undefined,
          site_id: executionContext.siteId || undefined,
          action_type: preset.actionType || 'manual_note',
          message,
        },
      });
      if (created) setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      if (selectedTicket?.id && shouldMirrorRunbookToTicket({ presetId: preset.id, actionType: preset.actionType })) {
        const headers = getAuthHeadersOrRedirect(navigate);
        if (!headers) return;
        const updated = await appendTicketUpdate({
          headers,
          ticketId: selectedTicket.id,
          note: buildTicketShortcutNote(preset.label, 'runbook'),
        });
        setTickets((prev) => prev.map((ticket) => (String(ticket.id) === String(updated.id) ? updated : ticket)));
      }
      toast.success(`Preset "${preset.label}" tercatat.`);
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menjalankan preset runbook.'));
    } finally {
      setSaving(false);
    }
  };

  const handleTicketShortcut = async (shortcut) => {
    if (!selectedTicket?.id || !shortcut) return;
    const headers = getAuthHeadersOrRedirect(navigate);
    if (!headers) return;
    setSaving(true);
    try {
      const status = String(shortcut.status || '').toLowerCase();
      const payload = {
        status,
        priority: detailDraft.priority,
        assigned_to: detailDraft.assigned_to || undefined,
      };
      const updatedTicket = await patchTicket({ headers, ticketId: selectedTicket.id, payload });
      const worklogNote = buildTicketShortcutNote(shortcut.label, selectedTicket.ticketCode || selectedTicket.id);
      const updatedWithLog = await appendTicketUpdate({
        headers,
        ticketId: selectedTicket.id,
        note: worklogNote,
      });
      setTickets((prev) => prev.map((ticket) => {
        if (String(ticket.id) !== String(updatedWithLog.id)) return ticket;
        return { ...updatedTicket, ...updatedWithLog };
      }));
      setDetailDraft((prev) => ({ ...prev, status }));
      if (executionContext) {
        const created = await createExecutionLog({
          apiBase: API,
          navigate,
          payload: {
            source_type: executionContext.sourceType,
            source_id: executionContext.sourceId,
            incident_id: executionContext.incidentId || undefined,
            ticket_id: executionContext.ticketId || undefined,
            customer_id: executionContext.customerId || undefined,
            router_id: executionContext.routerId || undefined,
            node_id: executionContext.nodeId || undefined,
            site_id: executionContext.siteId || undefined,
            action_type: shortcut.actionType || 'ticket_updated',
            message: worklogNote,
          },
        });
        if (created) setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      toast.success(`Shortcut "${shortcut.label}" berhasil.`);
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menjalankan shortcut ticket.'));
    } finally {
      setSaving(false);
    }
  };

  const logTicketNavigation = async (actionType, message) => {
    if (!executionContext) return;
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: executionContext.sourceType,
          source_id: executionContext.sourceId,
          incident_id: executionContext.incidentId || undefined,
          ticket_id: executionContext.ticketId || undefined,
          customer_id: executionContext.customerId || undefined,
          router_id: executionContext.routerId || undefined,
          node_id: executionContext.nodeId || undefined,
          site_id: executionContext.siteId || undefined,
          action_type: actionType,
          message,
        },
      });
      if (created) setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
    } catch (_error) {
      // keep navigation non-blocking
    }
  };

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/tickets" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Ticketing"
          title="Daftar Ticket Operasional"
          subtitle="Pantau ticket aktif, update status/PIC, dan lanjutkan investigasi ke Recovery, Topology, Customers, atau Infrastructure."
          action={(
            <UIButton variant="ghost" onClick={loadTickets} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari ticket code, judul, PIC, atau context..."
            className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:max-w-sm"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
          >
            <option value="all">Semua status</option>
            <option value="open">open</option>
            <option value="investigating">investigating</option>
            <option value="mitigated">mitigated</option>
            <option value="closed">closed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
          >
            <option value="all">Semua priority</option>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="critical">critical</option>
          </select>
          <select
            value={assigneeFilter}
            onChange={(event) => setAssigneeFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
          >
            <option value="all">Semua PIC</option>
            <option value="-">Tanpa PIC</option>
            {assigneeOptions.map((item) => (
              <option key={`pic-${item}`} value={String(item).toLowerCase()}>{item}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(event) => setIncludeClosed(event.target.checked)}
            />
            Include closed
          </label>
          <span className="ml-auto text-xs text-slate-500">
            Aktif: {activeCount} | Total: {tickets.length}
          </span>
        </div>

        <SectionContainer className="mb-4">
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => { setStatusFilter('all'); setIncludeClosed(false); }}
              className="rounded-md border border-white/15 bg-[#0a1322] px-2 py-1 text-[11px] text-slate-200 hover:border-white/35"
            >
              Active {filteredCounts.active}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('investigating')}
              className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100 hover:bg-amber-500/20"
            >
              Investigating {filteredCounts.investigating}
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('mitigated')}
              className="rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-1 text-[11px] text-sky-100 hover:bg-sky-500/20"
            >
              Mitigated {filteredCounts.mitigated}
            </button>
            <button
              type="button"
              onClick={() => setPriorityFilter('high')}
              className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-100 hover:bg-rose-500/20"
            >
              High/Critical {filteredCounts.highCritical}
            </button>
          </div>
        </SectionContainer>

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
          <SectionContainer className="space-y-2">
            <div className="overflow-auto rounded-lg border border-white/10">
              <table className="min-w-full divide-y divide-white/10 text-left text-sm">
                <thead className="bg-[#111b2d] text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-2">Ticket</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Priority</th>
                    <th className="px-3 py-2">PIC</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">Memuat ticket...</td>
                    </tr>
                  ) : null}
                  {!loading && filteredTickets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">
                        {tickets.length === 0
                          ? 'Belum ada ticket. Ticket dari Recovery, Topology, Customers, atau Infrastructure akan muncul di sini.'
                          : 'Tidak ada ticket yang cocok dengan filter.'}
                      </td>
                    </tr>
                  ) : null}
                  {!loading && filteredTickets.map((ticket) => {
                    const active = String(selectedTicketId) === String(ticket.id);
                    const relevant = String(ticket.id) === String(mostRelevantTicketId || '');
                    return (
                      <tr
                        key={ticket.id}
                        onClick={() => setSelectedTicketId(String(ticket.id || ''))}
                        className={`cursor-pointer transition-colors ${
                          active
                            ? 'bg-cyan-500/15 ring-1 ring-cyan-400/40'
                            : relevant
                              ? 'bg-amber-500/10'
                              : 'hover:bg-white/5'
                        }`}
                      >
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-start gap-2">
                            {active ? <span className="mt-1 h-2 w-2 rounded-full bg-cyan-300" /> : relevant ? <span className="mt-1 h-2 w-2 rounded-full bg-amber-300" /> : <span className="mt-1 h-2 w-2 rounded-full bg-transparent" />}
                            <div>
                              <p className="text-sm font-semibold text-white">{ticket.ticketCode || ticket.id}</p>
                              <p className="text-xs text-slate-300">{ticket.title || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top"><StatusBadge tone={statusTone(ticket.status)}>{String(ticket.status || 'open').toLowerCase()}</StatusBadge></td>
                        <td className="px-3 py-2 align-top"><StatusBadge tone={priorityTone(ticket.priority)}>{String(ticket.priority || 'medium').toLowerCase()}</StatusBadge></td>
                        <td className="px-3 py-2 align-top text-xs text-slate-300">{ticket.assignedTo || '-'}</td>
                        <td className="px-3 py-2 align-top text-xs text-slate-300">{ticket.sourceType || '-'}:{ticket.sourceId || '-'}</td>
                        <td className="px-3 py-2 align-top text-xs text-slate-400">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionContainer>

          <SectionContainer className="space-y-3">
            {!selectedTicket ? (
              <p className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">
                Pilih ticket dari daftar untuk melihat detail dan update worklog.
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedTicket.title || '-'}</p>
                      <p className="text-xs text-slate-400">{selectedTicket.ticketCode || selectedTicket.id}</p>
                    </div>
                    <StatusBadge tone={statusTone(selectedTicket.status)}>{String(selectedTicket.status || 'open').toLowerCase()}</StatusBadge>
                  </div>
                  <p className="text-xs text-slate-300">{selectedTicket.description || '-'}</p>
                  <p className="mt-2 text-[11px] text-slate-500">Context: {toContextSummary(selectedTicket)}</p>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Impact Summary</p>
                    <StatusBadge tone={ticketImpactSummary.severityTone}>{String(ticketImpactSummary.severityLabel || 'low').toUpperCase()}</StatusBadge>
                  </div>
                  {ticketImpactSummary.state !== 'ok' ? (
                    <p className="text-xs text-slate-400">{ticketImpactSummary.message}</p>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge tone="neutral">Impacted {ticketImpactSummary.counts.total}</StatusBadge>
                        <StatusBadge tone={ticketImpactSummary.counts.offline > 0 ? 'danger' : 'neutral'}>Offline {ticketImpactSummary.counts.offline}</StatusBadge>
                        <StatusBadge tone={ticketImpactSummary.counts.degraded > 0 ? 'warn' : 'neutral'}>Degraded {ticketImpactSummary.counts.degraded}</StatusBadge>
                      </div>
                      {ticketImpactSummary.sites.length > 0 ? (
                        <p className="text-[11px] text-slate-400">
                          Site impacted: {ticketImpactSummary.sites.slice(0, 2).map((item) => `${item.site} (${item.total})`).join(' | ')}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-500">Site impact belum tersedia dari context ticket ini.</p>
                      )}
                      {ticketImpactSummary.topCustomers.length > 0 ? (
                        <div className="space-y-1">
                          {ticketImpactSummary.topCustomers.slice(0, 3).map((customer) => (
                            <p key={customer.id} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5 text-[11px] text-slate-300">
                              {customer.name} ({customer.code}) | {String(customer.state || 'unknown').toUpperCase()}
                            </p>
                          ))}
                          {ticketImpactSummary.counts.total > 3 ? (
                            <button
                              type="button"
                              onClick={() => setShowImpactDrawer(true)}
                              className="text-[11px] text-cyan-300 hover:text-cyan-200"
                            >
                              +{ticketImpactSummary.counts.total - 3} lainnya | Lihat semua
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Update Ticket</p>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {TICKET_SHORTCUTS.map((shortcut) => (
                      <button
                        key={shortcut.id}
                        type="button"
                        onClick={() => handleTicketShortcut(shortcut)}
                        disabled={saving}
                        className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {shortcut.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid gap-2 md:grid-cols-3">
                    <label className="space-y-1">
                      <span className="text-[11px] text-slate-300">Status</span>
                      <select
                        value={detailDraft.status}
                        onChange={(event) => setDetailDraft((prev) => ({ ...prev, status: event.target.value }))}
                        className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100"
                      >
                        <option value="open">open</option>
                        <option value="investigating">investigating</option>
                        <option value="mitigated">mitigated</option>
                        <option value="closed">closed</option>
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px] text-slate-300">Priority</span>
                      <select
                        value={detailDraft.priority}
                        onChange={(event) => setDetailDraft((prev) => ({ ...prev, priority: event.target.value }))}
                        className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-[11px] text-slate-300">PIC / Owner</span>
                      <input
                        value={detailDraft.assigned_to}
                        onChange={(event) => setDetailDraft((prev) => ({ ...prev, assigned_to: event.target.value }))}
                        placeholder="PIC"
                        className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                      />
                    </label>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <UIButton variant="subtle" onClick={handleSaveDetail} disabled={saving}>Simpan Perubahan</UIButton>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Runbook Preset</p>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {RUNBOOK_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleRunbookPreset(preset)}
                        disabled={saving}
                        className="rounded-md border border-white/15 bg-[#0f1a2b] px-2 py-1 text-[11px] text-slate-200 hover:border-cyan-400/60 hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500">Preset ini mencatat execution log dan menambahkan worklog ringkas pada ticket aktif.</p>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Last Operational Update</p>
                  {latestExecutionLog ? (
                    <div className="space-y-1 text-xs text-slate-300">
                      <p className="font-semibold text-white">{getExecutionActionLabel(latestExecutionLog.actionType)}</p>
                      <p>{latestExecutionLog.message || '-'}</p>
                      <p className="text-[11px] text-slate-500">
                        {latestExecutionLog.actor || '-'} | {latestExecutionLog.createdAt ? new Date(latestExecutionLog.createdAt).toLocaleString('id-ID') : '-'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Belum ada tindakan tercatat.</p>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Worklog</p>
                  {visibleLogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada worklog.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {visibleLogs.map((entry) => (
                        <p key={entry.id || `${entry.created_at}-${entry.message}`} className="text-[11px] text-slate-300">
                          {entry.message}
                          <span className="text-slate-500"> ({entry.actor || '-'} | {entry.created_at ? new Date(entry.created_at).toLocaleString('id-ID') : '-'})</span>
                        </p>
                      ))}
                    </div>
                  )}
                  {(selectedTicket.actionLog || []).length > 3 ? (
                    <button
                      type="button"
                      className="mt-2 text-[11px] text-cyan-300 hover:text-cyan-200"
                      onClick={() => setShowAllLogs((prev) => !prev)}
                    >
                      {showAllLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua log'}
                    </button>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <input
                      value={worklogNote}
                      onChange={(event) => setWorklogNote(event.target.value)}
                      placeholder="Tulis update investigasi atau tindakan yang dilakukan"
                      className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <UIButton variant="ghost" onClick={handleAddWorklog} disabled={saving}>Tambah Worklog</UIButton>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Execution Log</p>
                    <span className="text-[10px] text-slate-500">Total {executionLogs.length}</span>
                  </div>
                  {executionLoading ? <p className="text-xs text-slate-400">Masih memuat data...</p> : null}
                  {!executionLoading && executionError ? <p className="text-xs text-rose-300">{executionError}</p> : null}
                  {!executionLoading && !executionError && executionLogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada data terkait. Langkah operasional akan muncul di sini.</p>
                  ) : null}
                  {!executionLoading && !executionError && executionLogs.length > 0 ? (
                    <div className="space-y-1.5">
                      {visibleExecutionLogs.map((entry) => (
                        <div key={entry.id || `${entry.createdAt}-${entry.message}`} className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1.5">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-slate-100">{getExecutionActionLabel(entry.actionType)}</p>
                            <StatusBadge tone={executionActionTone(entry.actionType)}>{entry.actor || '-'}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{entry.message}</p>
                          <p className="mt-1 text-[10px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                        </div>
                      ))}
                      {executionLogs.length > 5 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllExecutionLogs((prev) => !prev)}
                        >
                          {showAllExecutionLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <input
                      value={executionNote}
                      onChange={(event) => setExecutionNote(event.target.value)}
                      placeholder="Tambah catatan runbook operasional"
                      className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <UIButton variant="ghost" onClick={handleAddExecutionNote} disabled={saving}>Simpan Note</UIButton>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <UIButton
                    as="link"
                    to={buildRecoveryLink(selectedTicket)}
                    variant="ghost"
                    onClick={() => logTicketNavigation('recovery_opened', `Recovery dibuka dari ticket ${selectedTicket.ticketCode || selectedTicket.id}.`)}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    Open in Recovery
                  </UIButton>
                  <UIButton
                    as="link"
                    to={buildTopologyLink(selectedTicket)}
                    variant="subtle"
                    onClick={() => logTicketNavigation('topology_opened', `Topology dibuka dari ticket ${selectedTicket.ticketCode || selectedTicket.id}.`)}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    Open in Topology
                  </UIButton>
                  <UIButton as="link" to={buildInfrastructureLink(selectedTicket)} variant="ghost">
                    Open in Infrastructure
                  </UIButton>
                  <UIButton as="link" to={buildCustomerLink(selectedTicket)} variant="ghost">
                    View Customer
                  </UIButton>
                </div>
              </>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
      <CustomerImpactDrawer
        open={showImpactDrawer}
        onClose={() => setShowImpactDrawer(false)}
        title="Semua Customer Terdampak"
        subtitle={selectedTicket ? `Context ticket ${selectedTicket.ticketCode || selectedTicket.id}` : ''}
        customers={ticketImpactSummary.customers || []}
        recoveryIncidentId={selectedTicket?.incidentId || ''}
      />
    </AppShell>
  );
}
